import {
    InformationCircleIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
    ExclamationTriangleIcon,
    SparklesIcon,
    XMarkIcon,
    ArrowTopRightOnSquareIcon,
} from '@heroicons/react/20/solid';
import { clsx } from 'clsx';
import ButtonV2, { ButtonV2Props } from '../button/ButtonV2';
import { Link } from 'react-router-dom';
import React, { ReactNode } from 'react';

export type AlertV2Props = {
    type: 'info' | 'success' | 'warn' | 'error' | 'primary';
    title?: string;
    description?: ReactNode | ReactNode[];
    onClose?: () => void;
    children?: ReactNode | ReactNode[];
    className?: string;
    showIcon?: boolean;
    size?: 'sm' | 'md';
};

function AlertV2({ type, title, description, onClose, children, className, showIcon = true, size = 'md' }: AlertV2Props): JSX.Element {
    return (
        <div
            className={clsx(
                'rounded-md border flex items-start gap-4',
                {
                    'border-blue-200 bg-blue-50': type === 'info',
                    'border-green-200 bg-green-50': type === 'success',
                    'border-red-200 bg-red-50': type === 'error',
                    'border-orange-200 bg-orange-50': type === 'warn',
                    'border-indigo-200 bg-indigo-50': type === 'primary',
                },
                {
                    'p-4': size === 'md',
                    'p-2': size === 'sm',
                },
                className
            )}
        >
            {/* Icons */}
            {showIcon && (
                <span className="h-5 w-5 shrink-0" aria-hidden="true">
                    {type === 'info' && <InformationCircleIcon className="text-blue-400" />}
                    {type === 'success' && <CheckCircleIcon className="text-green-400" />}
                    {type === 'warn' && <ExclamationCircleIcon className="text-orange-400" />}
                    {type === 'error' && <ExclamationTriangleIcon className="text-red-400" />}
                    {type === 'primary' && <SparklesIcon className="text-indigo-400" />}
                </span>
            )}
            {/* Content */}
            <div className="flex flex-wrap gap-x-2 gap-y-1">
                <p className={`font-medium text-gray-800 ${size === 'md' ? 'text-sm' : 'text-xs'}`}>{title}</p>
                {Array.isArray(description) ? (
                    <ul className="w-full space-y-1">
                        {description.map((d, i) => {
                            return (
                                <li key={i} className={`text-gray-600 text-left ${size === 'md' ? 'text-sm' : 'text-xs'}`}>
                                    {d}
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <p className={`text-gray-600 text-left ${size === 'md' ? 'text-sm' : 'text-xs'}`}>{description}</p>
                )}

                {React.Children.count(children) > 0 && <div className="basis-full mt-2 gap-4 flex items-center flex-wrap">{children}</div>}
            </div>
            {onClose && (
                <button className="ml-auto" onClick={onClose}>
                    <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
            )}
        </div>
    );
}

function Action(props: ButtonV2Props): JSX.Element {
    return <ButtonV2 {...props} size="xs" type="secondary" />;
}

type SecondaryActionProps = {
    label: string;
    onClick: () => void;
};
function SecondaryAction({ label, onClick }: SecondaryActionProps): JSX.Element {
    return (
        <button onClick={onClick} className="text-gray-600 text-sm outline-none ring-0">
            {label}
        </button>
    );
}

type InternalLinkProps = {
    to: string;
    label: string;
};
function InternalLink({ to, label }: InternalLinkProps): JSX.Element {
    return (
        <Link className="text-gray-600 text-sm" to={to}>
            {label}
        </Link>
    );
}

type ExternalLinkProps = {
    href: string;
    label: string;
};
function ExternalLink({ href, label }: ExternalLinkProps): JSX.Element {
    return (
        <a href={href} target="_blank" className="text-gray-600 text-sm flex items-center gap-1 w-fit" rel="noreferrer">
            {label}
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </a>
    );
}

const _AlertV2 = Object.assign(AlertV2, { Action, SecondaryAction, InternalLink, ExternalLink });

export default _AlertV2;
